import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, updateDoc, doc, orderBy } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../utils/firebaseConfig';
import { cacheService, CACHE_KEYS } from '../utils/cacheService';
import GeneralNavbar from './GeneralNavbar';
import './EditProduct.css';

function EditOffer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    discount: '',
    category: '',
    price: '',  // ➡ Nuevo campo de precio
    stock: '',  // ➡ Nuevo campo de stock
  });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [offerId, setOfferId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);

  const handleBackClick = () => {
    navigate('/admin/offers');
  };

  // 🔹 Cargar categorías al montar el componente
  useEffect(() => {
    loadCategories();
  }, []);

  // 🔹 Cargar lista de categorías (con caché)
  const loadCategories = async () => {
    try {
      // 🚀 Verificar caché primero
      const cached = cacheService.get(CACHE_KEYS.CATEGORIES);
      if (cached) {
        setCategories(cached);
        return;
      }

      // Si no hay caché, cargar de Firebase
      const q = query(collection(db, 'categories'), orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      
      const categoryList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      
      // 💾 Guardar en caché
      cacheService.set(CACHE_KEYS.CATEGORIES, categoryList);
      
      setCategories(categoryList);
    } catch (error) {
      console.error("Error cargando categorías:", error);
    }
  };

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        if (!id) {
          console.error('No se proporcionó un ID de oferta');
          return;
        }

        const offersRef = collection(db, 'offers');
        const q = query(offersRef, where('id', '==', Number(id)));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            const offerData = doc.data();
            setOffer(offerData);
            setOfferId(doc.id);

            setFormData({
              name: offerData.name,
              description: offerData.description,
              discount: offerData.discount || '',
              category: offerData.category || '',
              price: offerData.price || '',  // ➡ Se carga el precio existente
              stock: offerData.stock || '',  // ➡ Se carga el stock existente
            });
            setPreview(offerData.image);
          });
        } else {
          console.error('Oferta no encontrada en Firestore.');
        }
      } catch (error) {
        console.error('Error al obtener la oferta: ', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOffer();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLoading(true);
    try {
      let imageUrl = offer?.image || '';

      if (image) {
        const storage = getStorage();
        const imageRef = ref(storage, `offer-images/${id}-${image.name}`);
        await uploadBytes(imageRef, image);
        imageUrl = await getDownloadURL(imageRef);
      }

      const offerRef = doc(db, 'offers', offerId);
      await updateDoc(offerRef, {
        ...formData,
        discount: formData.discount ? parseInt(formData.discount) : 0,
        price: formData.price ? parseFloat(formData.price) : 0,  // ➡ Guardar precio como número
        stock: formData.stock ? parseInt(formData.stock) : 0,    // ➡ Guardar stock como número
        image: imageUrl,
      });

      navigate('/admin/offers');
    } catch (error) {
      console.error('Error actualizando la oferta: ', error);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="offer-form-wrapper">
      <GeneralNavbar title="Editar Combo" onBackClick={handleBackClick} />
      {loading ? (
        <p className="centered-text">Cargando...</p>
      ) : offer ? (
        <div className="offer-form-container">
          <form onSubmit={handleSubmit}>
            <div className="offer-form-group">
              <label className="offer-form-label">Título del Combo:</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="offer-form-input"
                required
              />
            </div>
            <div className="offer-form-group">
              <label className="offer-form-label">Descripción:</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="offer-form-input"
                required
              />
            </div>
            <div className="offer-form-group">
              <label className="offer-form-label">Precio:</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className="offer-form-input"
                required
              />
            </div>
            <div className="offer-form-group">
              <label className="offer-form-label">Stock:</label>
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                className="offer-form-input"
                required
              />
            </div>
            <div className="offer-form-group">
              <label className="offer-form-label">Categoría:</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="offer-form-input"
                required
              >
                <option value="">Selecciona una categoría</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="offer-form-group">
              <label className="offer-form-label">Descuento (%):</label>
              <input
                type="number"
                name="discount"
                value={formData.discount}
                onChange={handleChange}
                className="offer-form-input"
                placeholder="Ej: 10 para 10%"
              />
            </div>
            <div className="offer-form-group">
              <label className="offer-form-label">Imagen del Combo:</label>
              <input type="file" accept="image/*" onChange={handleImageChange} className="offer-form-input" />
              {preview && <img src={preview} alt="Vista previa" className="product-form-preview" />}
            </div>
            <button type="submit" className="offer-form-button" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </form>
        </div>
      ) : (
        <p className="centered-text">Oferta no encontrada.</p>
      )}
    </div>
  );
}

export default EditOffer;
